import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight, CalendarClock, Check, ChevronDown, ChevronRight, GripHorizontal,
  Mic, MicOff, Minimize2, PhoneOff, SkipForward, Sparkles, X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AltNotiz, OffeneWiedervorlagen } from '../Kundenkontext';
import { MitarbeiterAuswahl, OFFEN_FUER_ALLE } from '../MitarbeiterAuswahl';
import { useAuthStore } from '../../stores/authStore';
import { ladeKundenKontext, istOffen, type KundenKontext } from '../../lib/kundenKontext';
import { anrufFesthalten, useDialerStore } from '../../stores/dialerStore';
import { namensKlick, namensTitel } from '../../lib/akteBeimAnruf';
import { invalidateCache } from '../../lib/useCachedQuery';
import type { CallProtocolState, ProtocolSideStep, ProtocolStep } from '../../types';
import { NOT_SCHEDULED_REASONS, REASON_OTHER } from '../../lib/reasons';
import { ERGEBNIS_AUSWAHL, ERGEBNIS_INFO, type AnrufErgebnis } from '../../lib/anrufErgebnis';

const DISPOSITIONS = [
  '',
  'Vereinbart',
  'Nicht erreicht 1x', 'Nicht erreicht 2x', 'Nicht erreicht 3x',
  'Mail geschickt',
  'FollowUp 1', 'FollowUp 2',
  'Nicht vereinbart',
  'Warteliste',
  'Blacklist',
];

const DEFAULT_POS = { x: 0, y: 0 };  // 0,0 = center via translate(-50%, -50%)

function fmtTimer(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

// Formats a Date into the value a <input type="datetime-local"> expects (local).
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Quick-pick: N days from now at a fixed hour.
function quickPick(daysAhead: number, hour: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(hour, 0, 0, 0);
  return toLocalInput(d);
}

export function InCallModal() {
  const {
    status, current, callSid, attemptKey, dialedNumber, callAttempt, callStartedAt,
    muted, protocolTemplate, toggleMute, endCurrent, next, settings,
    callWindowCollapsed: collapsed, setCallWindowCollapsed: setCollapsed,
  } = useDialerStore();

  // Einmaliger Hinweis, dass sich das Fenster wegschieben und klein machen
  // lässt — beides gab es schon, nur wusste es niemand (Lilly, 19.08.2026).
  const [platzHinweis, setPlatzHinweis] = useState(
    () => localStorage.getItem('anruffenster-platz-hinweis') !== 'gesehen',
  );
  const platzHinweisWeg = () => {
    localStorage.setItem('anruffenster-platz-hinweis', 'gesehen');
    setPlatzHinweis(false);
  };
  const userId = useAuthStore((st) => st.user?.id);
  const { pathname } = useLocation();
  const [pos, setPos] = useState(DEFAULT_POS);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const [now, setNow] = useState(Date.now());

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [stepNotes, setStepNotes] = useState<Record<string, string>>({});
  const [sideStepsUsed, setSideStepsUsed] = useState<string[]>([]);
  const [freeNotes, setFreeNotes] = useState('');
  const [disposition, setDisposition] = useState<string>('');
  // Was ist am Telefon passiert? Erreicht, Mailbox, niemand dran … — getrennt
  // vom Kunden-Status, weil „Mailbox erwischt" kein Status ist (Destiny,
  // 17.08.2026). Vorbelegt aus dem, was die Telefonanlage gemeldet hat.
  const [ergebnis, setErgebnis] = useState<AnrufErgebnis | ''>('');
  // Grund für "Nicht vereinbart" — feste Liste, Pflicht (Sharina-Feedback
  // Juli 2026: „wenn wir keine Probestunde von uns aus vereinbaren können").
  const [notScheduledReason, setNotScheduledReason] = useState('');
  const [notScheduledOther, setNotScheduledOther] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const [callbackNote, setCallbackNote] = useState('');
  // Zuständig für die geplante Wiedervorlage: null = noch nichts gewählt
  // (dann ich selbst), '' = offen für alle, sonst eine Kollegin / ein Kollege.
  const [callbackAgent, setCallbackAgent] = useState<string | null>(null);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [showSideStep, setShowSideStep] = useState<ProtocolSideStep | null>(null);
  // Alt-Notiz und offene Wiedervorlagen der angerufenen Person.
  const [kontext, setKontext] = useState<KundenKontext | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Baseline vom letzten Anruf (Vorbefüllung): nur NEUE/geänderte Notizen
  // gehen beim Speichern zusätzlich in die Kunden-Akte — sonst würde jede
  // übernommene Notiz bei jedem Folgeanruf erneut in der Akte landen.
  const baseline = useRef<{ stepNotes: Record<string, string>; freeNotes: string }>({ stepNotes: {}, freeNotes: '' });
  const [prefilled, setPrefilled] = useState(false);

  const visible = status === 'in-call' || status === 'wrap-up';
  const isWrapUp = status === 'wrap-up';

  // Reset protocol state when a new call starts, then prefill from the
  // customer's last saved protocol so a repeat call continues where the
  // previous one left off (Sharina-Feedback Juli 2026).
  //
  // Hängt am Wählversuch-Zähler, nicht an der Anruf-Kennung: die kommt erst
  // verzögert von der Telefonanlage. Vorher lief dieser Block deshalb nur
  // ein einziges Mal (beim Laden der Seite) — die Vorbefüllung kam nie an,
  // und Haken aus dem vorherigen Gespräch standen beim nächsten Anruf noch
  // da (Sharina, 17.08.2026).
  useEffect(() => {
    setChecked({});
    setStepNotes({});
    setSideStepsUsed([]);
    setFreeNotes('');
    setDisposition('');
    setErgebnis('');
    setNotScheduledReason('');
    setNotScheduledOther('');
    setCallbackAt('');
    setCallbackNote('');
    setCallbackAgent(null);
    setExpandedStep(null);
    setShowSideStep(null);
    setError(null);
    setPrefilled(false);
    setKontext(null);
    baseline.current = { stepNotes: {}, freeNotes: '' };

    const customerId = useDialerStore.getState().current?.customer_id;
    if (!callAttempt || !customerId || customerId.startsWith('adhoc-')) return;
    let cancelled = false;
    // Was schon über die Person bekannt ist: die Notiz aus dem Altsystem und
    // offene Wiedervorlagen. Beides stand bisher nur in der Kundenakte — wer
    // im Power Dialer telefoniert, musste dafür zurückschalten (Lilly,
    // 25.08.2026). Gerade der Fall „Qualicall fertig, Termin erst nach den
    // Ferien" steht genau in diesen beiden Texten.
    void ladeKundenKontext(customerId).then((k) => {
      if (!cancelled) setKontext(k);
    });
    // Fortgeschriebener Skript-Stand des Kunden (aus Anrufen UND aus dem
    // Skript-Knopf im Kundenprofil) — nicht nur das letzte Anruf-Protokoll.
    void supabase.rpc('current_customer_protocol', { p_customer_id: customerId }).then(({ data }) => {
      if (cancelled || !data) return;
      const prev = data as CallProtocolState;
      // Nur übernehmen, wenn es dieselbe Vorlage ist — sonst passen die
      // Punkt-IDs nicht zusammen.
      const tpl = useDialerStore.getState().protocolTemplate;
      if (!tpl || prev.template_id !== tpl.id) return;
      const prevNotes = prev.step_notes ?? {};
      setChecked(prev.checked ?? {});
      setStepNotes(prevNotes);
      setFreeNotes(prev.free_notes ?? '');
      baseline.current = { stepNotes: { ...prevNotes }, freeNotes: prev.free_notes ?? '' };
      setPrefilled(true);
    });
    return () => { cancelled = true; };
  }, [callAttempt]);

  // Tick for live timer.
  useEffect(() => {
    if (status !== 'in-call' || !callStartedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [status, callStartedAt]);

  // Ergebnis vorbelegen, sobald die Nachbereitung aufgeht: wurde abgenommen,
  // steht „Erreicht" da, sonst „Niemand abgenommen". Ein Klick ändert es —
  // gerade der Mailbox-Fall kann nur die Person am Telefon beurteilen.
  useEffect(() => {
    if (status !== 'wrap-up') return;
    setErgebnis((v) => (v ? v : callStartedAt ? 'erreicht' : 'niemand'));
  }, [status, callStartedAt]);

  // Drag handlers (header only).
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      if (!dragStart.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPos({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  const startDrag = (e: React.MouseEvent) => {
    if (collapsed) return;
    dragStart.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
    setDragging(true);
  };

  const steps: ProtocolStep[] = protocolTemplate?.steps ?? [];
  const sideSteps: ProtocolSideStep[] = protocolTemplate?.side_steps ?? [];
  const mode = protocolTemplate?.mode ?? 'outcome';

  const elapsedMs = callStartedAt ? now - callStartedAt : 0;
  const completedCount = useMemo(() => steps.filter((s) => checked[s.id]).length, [steps, checked]);
  const requiredCount = useMemo(() => steps.filter((s) => s.required).length, [steps]);
  const requiredDone = useMemo(
    () => steps.filter((s) => s.required && checked[s.id]).length,
    [steps, checked],
  );

  // For flow mode: next step gates on previous step being checked.
  const nextFlowStepId = useMemo(() => {
    if (mode !== 'flow') return null;
    return steps.find((s) => !checked[s.id])?.id ?? null;
  }, [mode, steps, checked]);

  // For flow mode: auto-expand current step.
  useEffect(() => {
    if (mode === 'flow' && nextFlowStepId && !expandedStep) {
      setExpandedStep(nextFlowStepId);
    }
  }, [mode, nextFlowStepId, expandedStep]);

  // Beim Abhaken im Flow-Modus direkt den nächsten offenen Punkt aufklappen
  // (Skript + Notizfeld sichtbar), statt nur dessen Überschrift zu zeigen.
  const toggle = (id: string) => setChecked((p) => {
    const nextChecked = { ...p, [id]: !p[id] };
    if (mode === 'flow' && !p[id]) {
      const nextOpen = steps.find((s) => !nextChecked[s.id]);
      setExpandedStep(nextOpen?.id ?? null);
    }
    return nextChecked;
  });

  const applySideStep = (s: ProtocolSideStep) => {
    setSideStepsUsed((p) => (p.includes(s.id) ? p : [...p, s.id]));
    setShowSideStep(s);
  };

  const buildPayload = (): CallProtocolState => ({
    template_id: protocolTemplate?.id ?? null,
    template_name: protocolTemplate?.name,
    mode,
    checked,
    step_notes: stepNotes,
    side_steps_used: sideStepsUsed,
    free_notes: freeNotes,
    disposition: disposition || null,
    saved_at: new Date().toISOString(),
  });

  // "Nicht vereinbart" braucht einen Pflicht-Grund aus einer festen Liste
  // (Sharina, Juli 2026). Blockiert das Speichern, solange er fehlt.
  const notScheduledReasonMissing = disposition === 'Nicht vereinbart'
    && (!notScheduledReason || (notScheduledReason === REASON_OTHER && !notScheduledOther.trim()));

  // Nur in DIESEM Anruf neu erfasste Notizen — sie landen zusätzlich als
  // Notiz in der Kunden-Akte (Motive etc. für Profil & Standorte sichtbar).
  const buildNoteSummary = (): string | null => {
    const lines: string[] = [];
    if (disposition === 'Nicht vereinbart' && notScheduledReason) {
      const reasonText = notScheduledReason === REASON_OTHER
        ? `${REASON_OTHER}: ${notScheduledOther.trim()}`
        : notScheduledReason;
      lines.push(`Nicht vereinbart — Grund: ${reasonText}`);
    }
    for (const step of steps) {
      const now = (stepNotes[step.id] ?? '').trim();
      const before = (baseline.current.stepNotes[step.id] ?? '').trim();
      if (now && now !== before) lines.push(`${step.label}: ${now}`);
    }
    const fn = freeNotes.trim();
    if (fn && fn !== baseline.current.freeNotes.trim()) lines.push(fn);
    return lines.length ? 'Aus dem Anruf-Skript — ' + lines.join(' · ') : null;
  };

  const saveAndNext = async (advance: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const customerId = current && !current.ad_hoc ? current.customer_id : null;
      // EIN Weg für alles: Versuch, Ergebnis, Skript, Notiz und Status landen
      // gemeinsam an derselben Anruf-Zeile — gefunden über die Anruf-Kennung
      // oder den Versuchsschlüssel. Vorher hing das Speichern an der Kennung
      // der Telefonanlage; kam sie nicht, wurde still nichts gespeichert.
      if (attemptKey || callSid) {
        await anrufFesthalten({
          customerId,
          attemptKey: attemptKey ?? `v-nachtrag-${callSid}`,
          callSid,
          toNumber: dialedNumber ?? current?.phone_number ?? null,
          outcome: ergebnis || null,
          durationS: callStartedAt ? Math.round((now - callStartedAt) / 1000) : null,
          protocol: buildPayload(),
          disposition: disposition || null,
          noteSummary: buildNoteSummary(),
        });
      } else if (customerId) {
        // Kein Wählversuch bekannt (z. B. Fenster nach einem Neuladen wieder
        // aufgetaucht): Skript und Notizen trotzdem am Kunden festhalten,
        // statt sie zu verwerfen.
        const zusatz = disposition ? `Status: ${disposition}` : null;
        const notiz = [zusatz, buildNoteSummary()].filter(Boolean).join(' · ') || null;
        const { error } = await supabase.rpc('save_customer_protocol', {
          p_customer_id: customerId,
          p_protocol: buildPayload(),
          p_note_summary: notiz,
        });
        if (error) throw error;
      }
      // Nach dem Anruf auch Anrufliste & Dashboard auffrischen, damit der
      // gerade bearbeitete Lead dort sofort aktuell ist (kein Reload nötig).
      if (current) invalidateCache((k) =>
        k.startsWith('customers:') || k.startsWith('queue:') || k === 'dashboard'
        || k === `customer:${current.customer_id}`);
      // Optional follow-up: schedule a Wiedervorlage for this customer.
      if (callbackAt && current) {
        const zustaendig = callbackAgent ?? userId ?? OFFEN_FUER_ALLE;
        const offenFuerAlle = zustaendig === OFFEN_FUER_ALLE;
        const { error: cbErr } = await supabase.rpc('schedule_callback', {
          p_customer_id: current.customer_id,
          p_due_at: new Date(callbackAt).toISOString(),
          p_note: callbackNote.trim() || freeNotes.trim() || null,
          // Ohne Zuweisung wäre die Wiedervorlage bisher immer beim Anrufer
          // gelandet — jetzt ist auch „an eine Kollegin" und „offen für alle"
          // wählbar (Sharina, 27.08.2026).
          p_agent_id: offenFuerAlle ? null : zustaendig,
          p_unassigned: offenFuerAlle,
        });
        if (cbErr) throw cbErr;
        invalidateCache((k) => k.startsWith('callbacks:'));
      }
      if (advance && settings.auto_advance) {
        await next();
      } else {
        useDialerStore.setState({
          status: 'ready', callSid: null, attemptKey: null, dialedNumber: null,
          callStartedAt: null,
        });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // „Verwerfen" wirft nur die Nachbereitung weg, nicht den Anruf: der Versuch
  // ist beim Auflegen längst festgehalten. Ein ausdrücklich gewähltes Ergebnis
  // wird noch nachgetragen — es ist die Angabe, die dem Team am meisten hilft.
  const skip = async () => {
    const customerId = current && !current.ad_hoc ? current.customer_id : null;
    if (attemptKey && ergebnis) {
      await anrufFesthalten({
        customerId, attemptKey, callSid,
        toNumber: dialedNumber ?? current?.phone_number ?? null,
        outcome: ergebnis,
      }).catch((e) => console.error('Ergebnis konnte nicht nachgetragen werden', e));
    }
    if (settings.auto_advance) await next();
    else useDialerStore.setState({
      status: 'ready', callSid: null, attemptKey: null, dialedNumber: null, callStartedAt: null,
    });
  };

  if (!visible || !current) return null;

  // Direktwahl (Walk-in) hat keine Kundenakte — dann führt auch kein Name
  // dorthin. Vorher zeigten die Links im Kopf auf „/customers/adhoc-…" und
  // landeten auf einer leeren Seite.
  const akteId = current.ad_hoc ? null : current.customer_id;
  // Klick auf den Namen: in die Akte — oder, wenn die Akte schon hinter dem
  // Fenster liegt, das Fenster wegräumen, damit sie zu sehen ist.
  const akteKlick = namensKlick({
    ziel: `/customers/${akteId}`,
    pfad: pathname,
    fensterOffen: !collapsed,
  });
  const nameAufAkte = (e: React.MouseEvent) => {
    if (akteKlick !== 'fenster-wegraeumen') return;
    e.preventDefault();
    setCollapsed(true);
  };

  // Nur zeigen, wenn wirklich etwas drinsteht — ein leerer Kasten über dem
  // Skript kostet nur Platz.
  const kontextVorhanden = !!kontext
    && (!!kontext.altNotiz?.trim() || kontext.wiedervorlagen.some(istOffen));

  // Collapsed pill
  if (collapsed) {
    return (
      <div
        style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 95,
          background: isWrapUp ? '#fde68a' : 'var(--rot-dunkel)',
          color: isWrapUp ? '#78350f' : 'white',
          borderRadius: 999, padding: '10px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
          fontSize: 13, fontWeight: 600,
        }}
        onClick={() => setCollapsed(false)}
      >
        <span style={{
          width: 8, height: 8, borderRadius: 999,
          background: isWrapUp ? '#b45309' : '#86efac',
          animation: isWrapUp ? 'none' : 'pulse 1.4s infinite',
        }} />
        {isWrapUp ? 'Nachbereitung' : fmtTimer(elapsedMs)}
        {/* Auch klein gemacht führt der Name in die Akte — der Klick auf die
            Leiste selbst klappt weiter auf (Destiny, 08.09.2026). */}
        <span style={{ opacity: 0.8 }}>
          ·{' '}
          {akteId ? (
            <Link
              to={`/customers/${akteId}`}
              onClick={(e) => { e.stopPropagation(); nameAufAkte(e); }}
              style={akteLinkStil}
              title={namensTitel(akteKlick)}
            >
              {current.full_name}
            </Link>
          ) : current.full_name}
        </span>
        <span style={{ fontSize: 11, opacity: 0.7 }}>↑ aufklappen</span>
      </div>
    );
  }

  return (
    <>
      {/* Soft backdrop — clickable so user can still interact with the rest of the app. */}
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.12)',
        zIndex: 90, pointerEvents: 'none',
      }} />

      <div
        style={{
          position: 'fixed',
          left: '50%', top: '50%',
          transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
          width: 720, maxWidth: '95vw', maxHeight: '90vh',
          background: 'var(--surface)',
          border: '1px solid var(--border-color)', borderRadius: 14,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          zIndex: 100,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header — drag handle, customer info, timer, actions */}
        <div
          style={{
            padding: '12px 14px',
            background: isWrapUp ? '#fef3c7' : 'linear-gradient(180deg, var(--rot-dunkel), #8a1d28)',
            color: isWrapUp ? '#78350f' : 'white',
            display: 'flex', alignItems: 'center', gap: 12,
            cursor: dragging ? 'grabbing' : 'grab', userSelect: 'none',
          }}
          onMouseDown={startDrag}
        >
          <GripHorizontal size={18} style={{ opacity: 0.7 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: 999,
                background: isWrapUp ? '#b45309' : '#86efac',
                animation: isWrapUp ? 'none' : 'pulse 1.4s infinite',
              }} />
              <strong style={{ fontSize: 15 }}>
                {akteId ? (
                  <Link
                    to={`/customers/${akteId}`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={nameAufAkte}
                    style={akteLinkStil}
                    title={namensTitel(akteKlick)}
                  >
                    {current.full_name}
                  </Link>
                ) : current.full_name}
              </strong>
              <span style={{ fontSize: 12, opacity: 0.8 }}>· {current.phone_number}</span>
            </div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              {isWrapUp
                ? 'Nachbereitung — Protokoll vervollständigen und speichern'
                : `Live · ${fmtTimer(elapsedMs)} · ${completedCount}/${steps.length} Items`}
              <span style={{ opacity: 0.75 }}> · hier ziehen zum Verschieben</span>
            </div>
          </div>
          {/* Lilly, 19.08.2026: „Man kann die Termine teilweise nicht beim
              Telefonieren buchen, weil das kleine Anruffenster im Weg ist."
              Klein machen ging schon immer — nur sah man es dem Symbol nicht
              an. Jetzt steht es dran, und der Kopf sagt auch, dass sich das
              Fenster ziehen lässt. */}
          <button onClick={(e) => { e.stopPropagation(); setCollapsed(true); }}
            title="Fenster klein machen — es bleibt unten rechts erreichbar, das Gespräch läuft weiter"
            style={{ ...iconBtn(isWrapUp), width: 'auto', gap: 6, padding: '0 10px', fontSize: 12, fontWeight: 600 }}>
            <Minimize2 size={16} /> klein
          </button>
        </div>

        {platzHinweis && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', fontSize: 12,
            background: 'var(--surface-elevated)',
            borderBottom: '1px solid var(--border-color)',
            color: 'var(--text-secondary)',
          }}>
            <span style={{ flex: 1 }}>
              Fenster im Weg? Die <strong>Kopfzeile ziehen</strong> oder oben rechts auf{' '}
              <strong>„klein"</strong> — das Gespräch läuft weiter, und du kannst in Ruhe
              einen Termin buchen.
            </span>
            <button onClick={platzHinweisWeg}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', display: 'inline-flex', padding: 2,
              }}
              title="Verstanden — nicht mehr zeigen">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Stats strip — call live; in wrap-up shows summary */}
        <div style={{
          display: 'flex', gap: 16, padding: '10px 14px',
          background: 'var(--surface-elevated)', borderBottom: '1px solid var(--border-color)',
          fontSize: 12, color: 'var(--text-secondary)',
        }}>
          <Stat label="Dauer" value={callStartedAt ? fmtTimer(elapsedMs) : '—'} />
          <Stat label="Status" value={isWrapUp ? 'Nachbereitung' : 'Verbunden'} accent={isWrapUp ? '#b45309' : '#059669'} />
          <Stat label="Vorlage" value={protocolTemplate?.name ?? '(keine)'} />
          <Stat label="Pflicht-Items"
            value={requiredCount > 0 ? `${requiredDone}/${requiredCount}` : '—'}
            accent={requiredDone === requiredCount ? '#059669' : '#b45309'}
          />
          {akteId && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 14 }}>
              <a
                href={`/availability?customer=${akteId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--rot-dunkel)', fontWeight: 600 }}
              >
                Probetermin buchen ↗
              </a>
              {/* Dasselbe wie beim Namen: liegt das Profil schon hinter dem
                  Fenster, führt kein Sprung dorthin — dann räumt der Klick das
                  Fenster weg. Vorher „passierte einfach nichts". */}
              <Link
                to={`/customers/${akteId}`}
                onClick={nameAufAkte}
                title={namensTitel(akteKlick)}
                style={{ color: 'var(--rot-dunkel)', fontWeight: 600 }}
              >
                {akteKlick === 'fenster-wegraeumen' ? 'Kundenprofil zeigen →' : 'Kundenprofil →'}
              </Link>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Main: steps */}
          <div style={{
            flex: 1, overflow: 'auto', padding: 14,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {!protocolTemplate && (
              <div style={{ padding: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
                Lade Protokoll-Vorlage…
              </div>
            )}
            {prefilled && (
              <div style={{
                padding: '6px 10px', borderRadius: 6, fontSize: 12,
                background: 'color-mix(in srgb, var(--ton-blau) 12%, var(--surface))',
                color: 'var(--ton-blau)',
                border: '1px solid color-mix(in srgb, var(--ton-blau) 30%, transparent)',
              }}>
                Haken & Notizen vom letzten Anruf übernommen — einfach dort weitermachen.
              </div>
            )}

            {/* Was vor dem Gespräch schon bekannt ist. Steht ganz oben, weil es
                die erste Frage am Telefon beantwortet („warum rufe ich an?") —
                vorher musste man dafür ins Kundenprofil zurückschalten. */}
            {kontextVorhanden && (
              <div style={{
                padding: 10, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 10,
                border: '1px solid var(--border-color)', background: 'var(--surface-elevated)',
              }}>
                <OffeneWiedervorlagen rows={kontext?.wiedervorlagen ?? []} />
                <AltNotiz text={kontext?.altNotiz} kompakt />
              </div>
            )}
            {steps.map((step) => {
              const isChecked = !!checked[step.id];
              const isExpanded = expandedStep === step.id;
              const isCurrent = mode === 'flow' && nextFlowStepId === step.id;

              // Kein Punkt ist gesperrt. Die Reihenfolge führt weiter
              // („aktuell" + Aufklappen des nächsten Punktes), blockiert aber
              // nichts mehr: wer vorzeitig auflegt — kein Bedarf, Kunde weg —,
              // hakt direkt „Abschluss" ab und schreibt die Notiz, statt sich
              // vorher durch dreizehn Punkte zu klicken (Destiny, 17.08.2026).
              return (
                <div key={step.id}
                  style={{
                    border: '1px solid ' + (isCurrent ? 'var(--rot-dunkel)' : 'var(--border-color)'),
                    borderRadius: 8,
                    // Getönte Flächen statt fester Hellfarben: im dunklen Modus
                    // lag helle Schrift auf hellgrünem bzw. hellrotem Grund —
                    // gerade der aktuelle Punkt war kaum zu lesen.
                    background: isChecked
                      ? 'color-mix(in srgb, var(--ton-gruen) 12%, var(--surface))'
                      : isCurrent
                        ? 'color-mix(in srgb, var(--rot-dunkel) 10%, var(--surface))'
                        : 'var(--surface)',
                    transition: 'all 0.15s',
                  }}>
                  <div style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10,
                    cursor: 'pointer',
                  }}
                    onClick={() => setExpandedStep(isExpanded ? null : step.id)}>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggle(step.id); }}
                      style={{
                        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                        border: '2px solid ' + (isChecked ? 'var(--ton-gruen)' : 'var(--border-color)'),
                        background: isChecked ? 'var(--ton-gruen)' : 'transparent',
                        // Der Haken nimmt die Flächenfarbe des Fensters an, damit er
                        // hell wie dunkel auf dem Grün stehen kann.
                        color: 'var(--surface)',
                        cursor: 'pointer',
                        display: 'grid', placeItems: 'center', marginTop: 1,
                      }}
                    >
                      {isChecked && <Check size={14} />}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: 14,
                        color: isChecked ? 'var(--ton-gruen)' : 'var(--text-primary)',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {step.label}
                        {step.required && (
                          <span style={pillStyle('var(--ton-rot)')}>Pflicht</span>
                        )}
                        {isCurrent && (
                          <span style={pillStyle('var(--ton-gelb)')}>aktuell</span>
                        )}
                      </div>
                      {step.hint && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {step.hint}
                        </div>
                      )}
                    </div>
                    <button style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-secondary)', padding: 2,
                    }}>
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </div>

                  {isExpanded && (
                    <div style={{
                      borderTop: '1px solid var(--border-color)',
                      padding: 12,
                      background: 'var(--background)',
                      display: 'flex', flexDirection: 'column', gap: 8,
                    }}>
                      {step.script && (
                        <div style={{
                          padding: 10, background: 'var(--surface)', borderRadius: 6,
                          borderLeft: '3px solid var(--rot-dunkel)',
                          fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5,
                          color: 'var(--text-primary)',
                        }}>
                          {step.script.replace(/\{\{name\}\}/g, current.full_name.split(' ')[0] ?? '')
                            .replace(/\{\{kunde\.vorname\}\}/g, current.full_name.split(' ')[0] ?? '')}
                        </div>
                      )}
                      {step.notes_enabled !== false && (
                        <textarea
                          value={stepNotes[step.id] ?? ''}
                          onChange={(e) => setStepNotes((p) => ({ ...p, [step.id]: e.target.value }))}
                          placeholder="Notiz zu diesem Punkt…"
                          rows={2}
                          style={{
                            width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6,
                            border: '1px solid var(--border-color)', background: 'var(--surface)',
                            fontFamily: 'inherit', fontSize: 13, resize: 'vertical',
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Free notes */}
            <div style={{
              marginTop: 8, padding: 10,
              border: '1px solid var(--border-color)', borderRadius: 8,
              background: 'var(--surface)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Freier Kommentar (geht ins Anruflog)
              </div>
              <textarea
                value={freeNotes}
                onChange={(e) => setFreeNotes(e.target.value)}
                rows={3}
                placeholder="Alles weitere, was wichtig ist…"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6,
                  border: '1px solid var(--border-color)', background: 'var(--background)',
                  fontFamily: 'inherit', fontSize: 13, resize: 'vertical',
                }}
              />
            </div>

            {/* Wiedervorlage — schedule a follow-up without leaving the call flow */}
            {isWrapUp && (
              <div style={{
                marginTop: 8, padding: 10,
                border: '1px solid var(--border-color)', borderRadius: 8,
                background: 'var(--surface)',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CalendarClock size={14} /> Wiedervorlage planen (optional)
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: callbackAt ? 8 : 0 }}>
                  <button type="button" onClick={() => setCallbackAt(quickPick(1, 10))} style={chipBtn(callbackAt === quickPick(1, 10))}>Morgen 10:00</button>
                  <button type="button" onClick={() => setCallbackAt(quickPick(3, 10))} style={chipBtn(callbackAt === quickPick(3, 10))}>In 3 Tagen</button>
                  <button type="button" onClick={() => setCallbackAt(quickPick(7, 10))} style={chipBtn(callbackAt === quickPick(7, 10))}>In 1 Woche</button>
                  <input
                    type="datetime-local"
                    value={callbackAt}
                    onChange={(e) => setCallbackAt(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--surface)', fontSize: 13 }}
                  />
                  {callbackAt && (
                    <button type="button" onClick={() => { setCallbackAt(''); setCallbackNote(''); setCallbackAgent(null); }} style={chipBtn(false)}>✕ keine</button>
                  )}
                </div>
                {callbackAt && (
                  <>
                    <input
                      type="text"
                      value={callbackNote}
                      onChange={(e) => setCallbackNote(e.target.value)}
                      placeholder="Notiz zur Wiedervorlage (optional — sonst wird der Kommentar übernommen)…"
                      style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--background)', fontSize: 13 }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                      Zuständig:
                      <MitarbeiterAuswahl
                        wert={callbackAgent ?? userId ?? OFFEN_FUER_ALLE}
                        onChange={setCallbackAgent}
                        eigeneId={userId}
                        aria-label="Zuständig für die Wiedervorlage"
                        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--surface)', fontSize: 12 }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Side rail: snippets for objection-handling */}
          {sideSteps.length > 0 && (
            <div style={{
              width: 220, borderLeft: '1px solid var(--border-color)',
              padding: 12, overflow: 'auto',
              background: 'var(--surface-elevated)',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Sparkles size={12} /> Antworten
              </div>
              {sideSteps.map((s) => (
                <button
                  key={s.id}
                  onClick={() => applySideStep(s)}
                  style={{
                    textAlign: 'left', padding: '8px 10px', borderRadius: 6,
                    border: '1px solid var(--border-color)',
                    // Getönte Fläche statt festem Hellblau: im dunklen Modus stand
                    // sonst helle Schrift auf hellblauem Grund — die schon
                    // benutzte Antwort war nicht mehr lesbar.
                    background: sideStepsUsed.includes(s.id)
                      ? 'color-mix(in srgb, var(--ton-blau) 16%, var(--surface))'
                      : 'var(--surface)',
                    cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)',
                  }}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Side-step answer popover */}
        {showSideStep && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5,
          }}
            onClick={() => setShowSideStep(null)}>
            <div onClick={(e) => e.stopPropagation()} style={{
              width: 480, maxWidth: '90%',
              background: 'var(--surface)', borderRadius: 10,
              border: '1px solid var(--border-color)', padding: 18,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <strong>{showSideStep.label}</strong>
                <button onClick={() => setShowSideStep(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={16} />
                </button>
              </div>
              {/* Der Vorschlagstext folgt dem Hell/Dunkel-Modus. Vorher stand hier
                  eine fest helle Fläche, im dunklen Modus mit heller Schrift
                  darauf — der Tipp war nur lesbar, wenn man ihn markierte
                  (Kristin, Aug. 2026). */}
              <div style={{
                padding: 12, background: 'var(--surface-elevated)', borderRadius: 6,
                border: '1px solid var(--border-color)',
                borderLeft: '3px solid var(--rot-dunkel)',
                color: 'var(--text-primary)',
                whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: 14,
              }}>
                {showSideStep.answer}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: 12, borderTop: '1px solid var(--border-color)',
          background: 'var(--surface)',
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
        }}>
          {error && (
            <div style={{
              flex: '1 0 100%', padding: '6px 10px', background: '#fee', color: '#900',
              borderRadius: 6, fontSize: 13, marginBottom: 4,
            }}>{error}</div>
          )}

          {!isWrapUp ? (
            <>
              <button onClick={toggleMute} style={btn(muted)}>
                {muted ? <MicOff size={14} /> : <Mic size={14} />} {muted ? 'Stumm' : 'Mute'}
              </button>
              <button onClick={endCurrent} style={dangerBtn}>
                <PhoneOff size={14} /> Auflegen
              </button>
              <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
                Tipp: Beim Auflegen schaltet der Modal automatisch in den Nachbereitungs-Modus.
              </div>
            </>
          ) : (
            <>
              {/* Anruf-Ergebnis: was ist am Telefon passiert? Steht bewusst VOR
                  dem Kunden-Status — es beschreibt den Versuch, nicht den
                  Verkaufsstand, und ist die Angabe, die dem Team für den
                  nächsten Anruf am meisten sagt. */}
              <div style={{ flex: '1 0 100%', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Ergebnis:
                </span>
                {ERGEBNIS_AUSWAHL.map((e) => {
                  const info = ERGEBNIS_INFO[e];
                  const aktiv = ergebnis === e;
                  return (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setErgebnis(aktiv ? '' : e)}
                      title={info.label}
                      style={{
                        padding: '5px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                        fontWeight: aktiv ? 700 : 500,
                        border: '1px solid ' + (aktiv ? info.farbe : 'var(--border-color)'),
                        background: aktiv ? info.hintergrund : 'transparent',
                        color: aktiv ? info.farbe : 'var(--text-primary)',
                      }}
                    >
                      {info.kurz}
                    </button>
                  );
                })}
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  landet im Verlauf und in der Akte
                </span>
              </div>
              <select
                value={disposition}
                onChange={(e) => setDisposition(e.target.value)}
                title="Kunden-Status — wo steht die Person im Prozess?"
                style={{
                  padding: '8px 10px', borderRadius: 6,
                  border: '1px solid var(--border-color)', background: 'var(--surface)',
                  fontSize: 14, minWidth: 200,
                }}
              >
                {DISPOSITIONS.map((d) => (
                  <option key={d || '__none'} value={d}>{d || '— Status unverändert (Auto) —'}</option>
                ))}
              </select>
              {disposition === 'Nicht vereinbart' && (
                <>
                  <select
                    value={notScheduledReason}
                    onChange={(e) => setNotScheduledReason(e.target.value)}
                    style={{
                      padding: '8px 10px', borderRadius: 6, minWidth: 200, fontSize: 14,
                      background: 'var(--surface)',
                      border: '1px solid ' + (notScheduledReasonMissing ? '#dc2626' : 'var(--border-color)'),
                    }}
                  >
                    <option value="">— Grund wählen * —</option>
                    {NOT_SCHEDULED_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {notScheduledReason === REASON_OTHER && (
                    <input
                      value={notScheduledOther}
                      onChange={(e) => setNotScheduledOther(e.target.value)}
                      placeholder="Grund kurz beschreiben"
                      style={{
                        padding: '8px 10px', borderRadius: 6, fontSize: 14, minWidth: 180,
                        border: '1px solid var(--border-color)', background: 'var(--surface)',
                      }}
                    />
                  )}
                </>
              )}
              <button onClick={() => void skip()} style={btnGhost} disabled={saving}>
                <SkipForward size={14} /> Verwerfen
              </button>
              <button onClick={() => void saveAndNext(false)} style={btnGhost} disabled={saving || notScheduledReasonMissing}>
                <Check size={14} /> Speichern (Stop)
              </button>
              <button onClick={() => void saveAndNext(true)} style={btnPrimary} disabled={saving || notScheduledReasonMissing}>
                <Check size={14} /> {saving ? 'speichere…' : 'Speichern + Weiter'}
                <ArrowRight size={14} />
              </button>
              {notScheduledReasonMissing && (
                <span style={{ flex: '1 0 100%', color: '#b45309', fontSize: 12 }}>
                  Bei „Nicht vereinbart" ist der Grund Pflicht.
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: accent ?? 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

// Der Name ist ein Weg in die Kundenakte: gepunktet unterstrichen, damit man
// es sieht, ohne dass er wie ein Knopf aussieht (Destiny, 08.09.2026).
const akteLinkStil: React.CSSProperties = {
  color: 'inherit',
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: 3,
};

const baseBtn: React.CSSProperties = {
  padding: '8px 14px', borderRadius: 6, border: 'none',
  cursor: 'pointer', fontSize: 13, display: 'inline-flex',
  alignItems: 'center', gap: 6,
};

const btn = (active = false): React.CSSProperties => ({
  ...baseBtn,
  background: active ? '#fee2e2' : 'var(--surface-elevated)',
  color: active ? '#991b1b' : 'var(--text-primary)',
  border: '1px solid var(--border-color)',
});

const btnPrimary: React.CSSProperties = {
  ...baseBtn, background: 'var(--rot-dunkel)', color: 'white',
};

const btnGhost: React.CSSProperties = {
  ...baseBtn,
  background: 'transparent',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
};

const dangerBtn: React.CSSProperties = {
  ...baseBtn, background: '#dc2626', color: 'white',
};

const iconBtn = (light: boolean): React.CSSProperties => ({
  background: light ? 'rgba(120, 53, 15, 0.1)' : 'rgba(255,255,255,0.15)',
  border: 'none', borderRadius: 6, padding: 6,
  color: light ? '#78350f' : 'white', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center',
});

function chipBtn(active: boolean): React.CSSProperties {
  return {
    padding: '6px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
    border: '1px solid ' + (active ? 'var(--rot-dunkel)' : 'var(--border-color)'),
    background: active ? 'var(--rot-dunkel)' : 'var(--surface)',
    color: active ? 'white' : 'var(--text-primary)',
  };
}

// Ein Ton pro Kennzeichnung, Fläche wird daraus getönt — wie bei den Badges
// in der Liste. So folgen „Pflicht" und „aktuell" dem hellen und dem dunklen
// Modus statt fest hell zu bleiben.
function pillStyle(ton: string): React.CSSProperties {
  return {
    fontSize: 10, padding: '2px 6px', borderRadius: 999,
    background: `color-mix(in srgb, ${ton} 11%, var(--surface))`,
    color: ton, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  };
}
